import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { formatDate } from "../../lib/datetime";
import { toast } from "sonner";
import { Trash2, Plus, FileText, User as UserIcon } from "lucide-react";
import Pagination, { PAGE_SIZE } from "../../components/Pagination";
import { useConfirm } from "../../components/ConfirmDialog";
import { useAuth } from "../../context/AuthContext";

const STATUSES = ["draft", "sent", "accepted", "rejected", "expired"];
const STATUS_LABEL = {
  draft: "Borrador",
  sent: "Enviada",
  accepted: "Aceptada",
  rejected: "Rechazada",
  expired: "Expirada",
};

export default function AdminProposals() {
  const [proposals, setProposals] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const nav = useNavigate();
  const confirm = useConfirm();
  const { isAdmin } = useAuth();

  const load = () => {
    const params = filter ? { status: filter } : {};
    api.get("/admin/proposals", { params }).then((r) => setProposals(r.data));
  };

  useEffect(() => {
    setPage(1);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Load sellers once (admin only) for the assign dropdown
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/users").then((r) => setSellers(r.data.filter((u) => u.role === "seller" && u.is_active))).catch(() => {});
  }, [isAdmin]);

  const assignOwner = async (proposalId, ownerId) => {
    try {
      const r = await api.patch(`/admin/proposals/${proposalId}/assign`, { owner_id: ownerId || null });
      const newOwnerName = ownerId ? sellers.find((s) => s.id === ownerId)?.name : null;
      setProposals((prev) =>
        prev.map((p) => (p.id === proposalId ? { ...p, ...r.data, owner_name: newOwnerName } : p)),
      );
      toast.success(ownerId ? "Vendedor asignado" : "Asignación removida");
    } catch (e) {
      toast.error("No se pudo asignar");
    }
  };

  const visible = proposals.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const remove = async (id) => {
    const ok = await confirm({
      title: "¿Eliminar propuesta?",
      message: "Esta acción es permanente. ¿Continuar?",
      confirmLabel: "Eliminar",
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/admin/proposals/${id}`);
    toast.success("Propuesta eliminada");
    setSelectedIds(new Set());
    load();
  };

  const toggleId = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const visibleIds = visible.map((p) => p.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const ok = await confirm({
      title: `¿Eliminar ${ids.length} propuesta${ids.length > 1 ? "s" : ""}?`,
      message: "Esta acción es permanente. ¿Continuar?",
      confirmLabel: "Eliminar",
      variant: "danger",
    });
    if (!ok) return;
    const { data } = await api.post("/admin/proposals/bulk-delete", { ids });
    toast.success(`${data.deleted} eliminada${data.deleted !== 1 ? "s" : ""}`);
    setSelectedIds(new Set());
    load();
  };

  const openPdf = async (id) => {
    const r = await api.get(`/admin/proposals/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    window.open(url, "_blank");
  };

  const fmtMoney = (v, currency) =>
    currency === "CLP"
      ? `$${Math.round(v).toLocaleString("es-CL")}`
      : `US$${(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div data-testid="admin-proposals-page" className="p-8 md:p-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand font-mono mb-2">Presupuestos · DoSoft</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-ash-100">Propuestas</h1>
          <p className="text-ash-300 text-sm mt-2">Cotizaciones formales con numeración, precios y PDF.</p>
        </div>
        <button
          data-testid="proposal-create-blank"
          onClick={() => nav("/admin/proposals/new")}
          className="inline-flex items-center gap-2 bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-5 py-3 uppercase tracking-wider text-xs"
        >
          <Plus size={14} /> Nueva propuesta
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-6">
        {selectedIds.size > 0 && (
          <div data-testid="bulk-bar" className="flex items-center gap-2 mr-2 px-3 py-1.5 border border-brand bg-brand/10 text-xs">
            <span className="text-brand font-mono">{selectedIds.size} seleccionada{selectedIds.size !== 1 ? "s" : ""}</span>
            <button
              data-testid="bulk-delete-btn"
              onClick={bulkDelete}
              className="inline-flex items-center gap-1.5 bg-[#9B1C1C] hover:bg-[#7e1717] text-white px-3 py-1.5 uppercase tracking-widest font-semibold"
            >
              <Trash2 size={12} /> Eliminar
            </button>
            <button onClick={() => setSelectedIds(new Set())} className="text-ash-300 hover:text-ash-100 ml-1">Cancelar</button>
          </div>
        )}
        <button
          onClick={() => setFilter("")}
          className={`px-4 py-2 border text-xs uppercase tracking-widest ${filter === "" ? "bg-brand text-ink-800 border-brand" : "border-ink-500 text-ash-300 hover:border-brand"}`}
        >
          Todas ({proposals.length})
        </button>
        {STATUSES.map((s) => (
          <button
            key={s}
            data-testid={`proposals-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`px-4 py-2 border text-xs uppercase tracking-widest ${filter === s ? "bg-brand text-ink-800 border-brand" : "border-ink-500 text-ash-300 hover:border-brand"}`}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      <div className="border border-ink-500 bg-white overflow-x-auto">
        <table data-testid="proposals-table" className="w-full text-sm">
          <thead>
            <tr className="bg-brand-slate text-left">
              <th className="px-3 py-3 w-10">
                <input type="checkbox" data-testid="proposals-select-all" checked={allVisibleSelected} onChange={toggleAllVisible} className="accent-brand cursor-pointer" />
              </th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white font-mono">N°</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white font-mono">Cliente</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white font-mono">Proyecto</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white font-mono">Total</th>
              {isAdmin && (
                <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white font-mono">Vendedor</th>
              )}
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white font-mono">Estado</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white font-mono">Fecha</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((p) => (
              <tr
                key={p.id}
                data-testid={`proposal-row-${p.id}`}
                className={`border-t border-gray-200 hover:bg-gray-50 cursor-pointer ${selectedIds.has(p.id) ? "bg-brand/5" : ""}`}
                onClick={() => nav(`/admin/proposals/${p.id}`)}
              >
                <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" data-testid={`proposal-select-${p.id}`} checked={selectedIds.has(p.id)} onChange={() => toggleId(p.id)} className="accent-brand cursor-pointer" />
                </td>
                <td className="px-4 py-3 font-mono text-ink-800 font-semibold">{p.number}</td>
                <td className="px-4 py-3">
                  <div className="text-ink-800 font-medium">{p.client_name}</div>
                  <div className="text-xs text-ash-500">{p.client_company || p.client_email}</div>
                </td>
                <td className="px-4 py-3 text-ink-800">{p.project_title}</td>
                <td className="px-4 py-3 font-mono text-ink-800">
                  {fmtMoney(p.total, p.currency)}
                  {p.total_monthly > 0 && (
                    <div className="text-[11px] text-[#D97757] font-mono mt-0.5">
                      + {fmtMoney(p.total_monthly, p.currency)}/mes
                    </div>
                  )}
                </td>
                {isAdmin && (
                  <td className="px-4 py-3 text-ink-800" onClick={(e) => e.stopPropagation()}>
                    <select
                      data-testid={`proposal-owner-select-${p.id}`}
                      value={p.owner_id || ""}
                      onChange={(e) => assignOwner(p.id, e.target.value || null)}
                      onClick={(e) => e.stopPropagation()}
                      className="bg-ash-100 border border-ink-500/30 hover:border-brand px-2 py-1.5 text-xs font-mono focus:border-brand outline-none cursor-pointer"
                      title="Asignar vendedor"
                    >
                      <option value="">Sin asignar</option>
                      {sellers.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                      {/* Show current owner even if not in active sellers list (deactivated/admin) */}
                      {p.owner_id && !sellers.find((s) => s.id === p.owner_id) && p.owner_name && (
                        <option value={p.owner_id}>{p.owner_name} (inactivo)</option>
                      )}
                    </select>
                  </td>
                )}
                <td className="px-4 py-3"><span className="text-xs uppercase tracking-widest text-brand font-mono">{STATUS_LABEL[p.status] || p.status}</span></td>
                <td className="px-4 py-3 text-ink-800 font-mono text-xs">{formatDate(p.created_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    onClick={(e) => { e.stopPropagation(); openPdf(p.id); }}
                    title="Ver PDF"
                    className="text-ash-500 hover:text-brand mr-3 inline-flex items-center"
                    data-testid={`proposal-pdf-${p.id}`}
                  >
                    <FileText size={16} strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); remove(p.id); }}
                    className="text-ash-500 hover:text-destructive"
                    data-testid={`proposal-delete-${p.id}`}
                  >
                    <Trash2 size={16} strokeWidth={1.5} />
                  </button>
                </td>
              </tr>
            ))}
            {proposals.length === 0 && (
              <tr><td colSpan={isAdmin ? 9 : 8} className="px-4 py-12 text-center text-brand-slate">
                No hay propuestas. Crea una desde el detalle de una solicitud o haz click en "Nueva propuesta".
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} total={proposals.length} onChange={setPage} />
    </div>
  );
}
